import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Img from "@insite/mobius/Img";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    image = "image",
    heading = "heading",
    subheading = "subheading",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        // [fields.background]: "image" | "color";
        [fields.image]: string;
        [fields.heading]: string;
        [fields.subheading]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
    };
    extendedStyles?: CENBannerStyles;
}

export interface CENBannerStyles {
    container?: InjectableCss;
    wrapper?: InjectableCss;
    /**
     * @deprecated Use the `bannerButton` property instead.
     */
    CENbannerButton?: ButtonPresentationProps;
    CENbannerHeading?: InjectableCss;
    CENbannerSubheading?: InjectableCss;
    img: InjectableCss;
    CENmainBannerWrapper?: InjectableCss;
}

export const CENbannerStyles: CENBannerStyles = {
    container: {
        css: css`
            position: relative;
            display: flex;
            flex-direction: column-reverse;
            margin-bottom: 104px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 56px;
                    `,
                    css`
                        margin-bottom: 56px;
                    `,
                    css`
                        margin-bottom: 76px;
                    `,
                ]);
            }}
            ::after {
                content: "";
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
                height: 100%;
                background: linear-gradient(
                    90deg,
                    #262525 13.02%,
                    rgba(99, 97, 97, 0.69) 47.92%,
                    rgba(143, 4, 70, 0.64) 100%
                );
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            display: none;
                        `,
                        css`
                            display: none;
                        `,
                        null,
                    ]);
                }}
            }
        `,
    },
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    CENmainBannerWrapper: {
        css: css`
            position: absolute;
            top: 56px;
            width: 45%;
            left: 107px;
            z-index: 100;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        position: unset;
                        width: unset;
                        transform: unset;
                    `,
                    css`
                        width: auto;
                        left: 75px;
                    `,
                    css`
                        width: auto;
                    `,
                ]);
            }}
        `,
    },
    CENbannerHeading: {
        css: css`
            color: ${COLORS.white};
            margin-bottom: 24px !important;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        color: ${getProp("theme.typography.p.color")} !important;
                        padding-top: 15px !important;
                    `,
                    null,
                    null,
                ]);
            }}
        `,
    },
    CENbannerSubheading: {
        css: css`
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 24px;
            margin-bottom: 40px !important;
            color: ${COLORS.white};
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 20px !important;
                            color: ${getProp("theme.typography.p.color")};
                            font-size: 18px !important;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    CENbannerButton: {
        mergeCss: true,
        css: css`
            min-height: 48px;
            background: ${COLORS.white};
            border-radius: 24px;
            width: 260px;
            padding: 12px 24px;
            border-color: ${COLORS.white};
            &:hover {
                border-color: ${COLORS.secondary.dark};
                background: ${COLORS.secondary.dark};
                color: ${COLORS.white};
            }
            span {
                font-weight: ${TYPOGRAPHY.weight.black};
                font-size: ${TYPOGRAPHY.size.large};
                line-height: 24px;
                color: ${COLORS.secondary.base};
                white-space: nowrap;
                &:hover {
                    color: ${COLORS.white};
                }
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            border-color: ${COLORS.secondary.base};
                            background: ${COLORS.secondary.base};
                            min-height: 40px !important;
                            span {
                                color: ${COLORS.white} !important;
                            }
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },

    img: {
        css: css`
            width: 100%;
            display: flex;
            background-position: center center;
            height: 376px;
            align-items: stretch;
            position: relative;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            height: auto !important;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
};

export const CENSectionBanner: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const { url } = useGetLink(fields.buttonLink);
    const history = useHistory();
    const { isInShell } = useShellContext();

    const onClick = () => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    const styles = useMergeStyles("banner", CENbannerStyles, extendedStyles);

    const wrapperStyles = {
        css: css`
            width: 100%;
            background-position: center center;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 20px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    };

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.CENmainBannerWrapper}>
                <StyledWrapper {...styles.CENbannerHeadingWrapper}>
                    <Typography variant="h1" {...styles.CENbannerHeading}>
                        {translate("{0}", fields.heading)}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...wrapperStyles}>
                    <Typography variant="p" {...styles.CENbannerSubheading}>
                        {parse(transformContentHtml(fields.subheading), parserOptions)}
                    </Typography>
                    <Button {...styles.CENbannerButton} onClick={onClick}>
                        {fields.buttonLabel || url}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
            <Img src={fields.image} loading="lazy" altText={fields.heading} {...styles.img} />
        </StyledWrapper>
    );
};

const CENbanner: WidgetModule = {
    component: CENSectionBanner,
    definition: {
        group: "Basic",
        icon: "image-landscape",
        fieldDefinitions: [
            {
                name: fields.image,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Banner Image",
            },
            {
                name: fields.heading,
                displayName: "Heading",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.subheading,
                displayName: "Sub Heading",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                fieldType: "Translatable",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
        ],
    },
};

export default CENbanner;
